import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { CheckCircle, Link as LinkIcon, Unlink, Settings as SettingsIcon, Shield, Bell } from 'lucide-react';
import type { AuthProviderType } from '../contexts/AWSAuthContext';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  animation: slideUp 0.5s ease-out;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  ${iosNativeCenterHeaderLeft}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const ProviderCard = styled(Card)<{ $linked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1.25rem;
  border: 2px solid ${props => props.$linked ? theme.colors.mint[200] : theme.colors.border.light};
  background: ${props => props.$linked ? theme.colors.glow.mint : 'white'};
`;

const ProviderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const ProviderIcon = styled.div<{ $provider: AuthProviderType }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => {
    switch (props.$provider) {
      case 'google': return '#fff';
      case 'password': return theme.colors.primary[100]};
    }
  };
  border: 1px solid ${theme.colors.border.light};
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ProviderDetails = styled.div`
  flex: 1;
`;

const ProviderName = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const ProviderStatus = styled.div<{ $linked: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  color: ${props => props.$linked ? theme.colors.mint[700] : theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  gap: 0.375rem;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActionButton = styled(Button)`
  min-width: 100px;
`;

const InfoBox = styled.div`
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${theme.colors.primary[400]};
  margin-bottom: 2rem;
  
  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.text.secondary};
    margin: 0;
    line-height: 1.6;
  }
`;

const providers: { type: AuthProviderType; name: string; description: string }[] = [
  { type: 'google', name: 'Google', description: 'Sign in with your Google account' },
  { type: 'password', name: 'Email & Password', description: 'Traditional email and password login' },
];

const SettingsPage: React.FC = () => {
  const { userProfile, linkProvider, unlinkProvider } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleLink = async (providerType: 'google') => {
    setLoading(providerType);
    try {
      await linkProvider(providerType);
    } catch (error) {
      console.error(`Error linking ${providerType}:`, error);
    } finally {
      setLoading(null);
    }
  };

  const handleUnlink = async (providerType: AuthProviderType) => {
    if (confirm(`Are you sure you want to unlink ${providerType}? You'll need at least one sign-in method.`)) {
      setLoading(providerType);
      try {
        await unlinkProvider(providerType);
      } catch (error) {
        console.error(`Error unlinking ${providerType}:`, error);
      } finally {
        setLoading(null);
      }
    }
  };

  const getProviderIcon = (provider: AuthProviderType) => {
    switch (provider) {
      case 'google':
        return (
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      case 'password':
        return <Shield size={24} color={theme.colors.primary[600]} />;
    }
  };

  const isLinked = (providerType: AuthProviderType) => {
    return userProfile?.providers.includes(providerType) || false;
  };

  const canUnlink = (providerType: AuthProviderType) => {
    return userProfile && userProfile.providers.length > 1 && isLinked(providerType);
  };

  return (
    <PageContainer>
      <Container>
        <Header>
          <Title>Account Settings</Title>
          <Subtitle>Manage your linked accounts and security ✨</Subtitle>
        </Header>

        <SectionTitle>
          <LinkIcon size={24} />
          Linked Accounts
        </SectionTitle>

        <InfoBox>
          <p>
            <strong>Link multiple sign-in methods</strong> to access your account from anywhere. 
            You can link Google or email/password to the same account.
          </p>
        </InfoBox>

        {providers.map((provider) => {
          const linked = isLinked(provider.type);
          const isCurrentlyLoading = loading === provider.type;
          
          return (
            <ProviderCard key={provider.type} $linked={linked}>
              <ProviderInfo>
                <ProviderIcon $provider={provider.type}>
                  {getProviderIcon(provider.type)}
                </ProviderIcon>
                <ProviderDetails>
                  <ProviderName>{provider.name}</ProviderName>
                  <ProviderStatus $linked={linked}>
                    {linked ? (
                      <>
                        <CheckCircle />
                        Connected
                      </>
                    ) : (
                      <>Not connected</>
                    )}
                  </ProviderStatus>
                </ProviderDetails>
              </ProviderInfo>
              
              {linked ? (
                <ActionButton
                  variant="outline"
                  size="sm"
                  onClick={() => handleUnlink(provider.type)}
                  disabled={!canUnlink(provider.type) || isCurrentlyLoading}
                >
                  {isCurrentlyLoading ? 'Unlinking...' : (
                    <>
                      <Unlink size={16} />
                      Unlink
                    </>
                  )}
                </ActionButton>
              ) : provider.type !== 'password' ? (
                <ActionButton
                  variant="primary"
                  size="sm"
                  onClick={() => handleLink(provider.type as 'google')}
                  disabled={isCurrentlyLoading}
                >
                  {isCurrentlyLoading ? 'Linking...' : (
                    <>
                      <LinkIcon size={16} />
                      Link
                    </>
                  )}
                </ActionButton>
              ) : (
                <ActionButton
                  variant="secondary"
                  size="sm"
                  disabled
                >
                  View Only
                </ActionButton>
              )}
            </ProviderCard>
          );
        })}

        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Button variant="outline" onClick={() => navigate('/profile')}>
            Back to Profile
          </Button>
        </div>
      </Container>
    </PageContainer>
  );
};

export default SettingsPage;

